import type { StyleSpecification } from 'maplibre-gl';

// On the web the map tiles and label glyphs come from a remote PBF server
// instead of the local .mbtiles and glyph files the native app uses. The style
// layers live in the bundled `webMapStyle.json`; the tile and glyph endpoints
// are configured separately in `tilesConfig.web.ts`. The style JSON is shared
// with desktop map tools, so its vector source may reference its own TileJSON
// `url` and its glyphs their own URL template; the web map always swaps those
// for `TILES_URL`/`GLYPHS_URL`, keeping the map data server in a single place.
import webMapStyle from '../config/webMapStyle.json';
import { GLYPHS_URL, TILES_URL } from '../config/tilesConfig.web';

export const MAP_CENTER = {
  lat: 35.694389,
  lng: 51.421389,
} as const;

export const INITIAL_ZOOM = 12;

const styleSources = webMapStyle.sources as Record<
  string,
  { type?: string; tiles?: string[] }
>;

const sourcesWithConfiguredTiles: Record<string, object> = {};
for (const [id, source] of Object.entries(styleSources)) {
  if (source.type === 'vector') {
    // Drop whatever TileJSON `url` the shared style JSON ships and point the
    // vector source at the configured PBF endpoint instead.
    sourcesWithConfiguredTiles[id] = { type: 'vector', tiles: [TILES_URL] };
  } else {
    sourcesWithConfiguredTiles[id] = source;
  }
}

const styleWithRemoteData = {
  ...webMapStyle,
  // Point glyph requests (fontstack/range pbfs) at the tile server's /fonts/
  // endpoint instead of whatever URL the shared style JSON ships with.
  glyphs: GLYPHS_URL,
  sources: sourcesWithConfiguredTiles,
};

// Bundled web style: layers come from `src/config/webMapStyle.json`, and every
// vector source renders from `TILES_URL` with label glyphs from `GLYPHS_URL`
// (both in `src/config/tilesConfig.web.ts`).
const mapStyle: StyleSpecification =
  styleWithRemoteData as unknown as StyleSpecification;

/**
 * Web counterpart of the native `createMapStyle` in `mapConfig.ts`. The web
 * style already renders from the remote tile/glyph server configured in
 * `tilesConfig.web.ts`, so the tile path (the local mbtiles file on native) is
 * unused - the parameter only keeps both platform variants' signatures in
 * sync.
 */
export const createMapStyle = (_tilePath: string): StyleSpecification =>
  mapStyle;

